/**
 * Quantity Input Component
 *
 * A reusable quantity selector with increment/decrement buttons.
 * Store-agnostic - can be used in any context.
 *
 * @package Enterprise\BookingSystem\Components
 */

/**
 * WordPress dependencies
 */
import { useId } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import './QuantityInput.scss';

export type QuantityInputProps = {
	/** Additional CSS class */
	readonly className?: string;
	/** Accessible label for decrement button */
	readonly decrementLabel?: string;
	/** Whether the input is disabled */
	readonly disabled?: boolean;
	/** Optional error message */
	readonly error?: string;
	/** Unique ID for the input (for accessibility) */
	readonly id?: string;
	/** Accessible label for increment button */
	readonly incrementLabel?: string;
	/** Optional label text */
	readonly label?: string;
	/** Maximum allowed quantity */
	readonly max?: number;
	/** Minimum allowed quantity */
	readonly min?: number;
	/** Callback when quantity changes */
	readonly onChange: (newQuantity: number) => void;
	/** Whether to show the label */
	readonly showLabel?: boolean;
	/** Size variant */
	readonly size?: 'large' | 'medium' | 'small';
	/** Current quantity value */
	readonly value: number;
};

export const QuantityInput = ({
	// eslint-disable-next-line @typescript-eslint/no-useless-default-assignment -- required by react/require-default-props for optional props
	id = undefined,
	value,
	onChange,
	min = 1,
	max = 100,
	disabled = false,
	// eslint-disable-next-line @typescript-eslint/no-useless-default-assignment -- required by react/require-default-props for optional props
	label = undefined,
	// eslint-disable-next-line @typescript-eslint/no-useless-default-assignment -- required by react/require-default-props for optional props
	error = undefined,
	className = '',
	decrementLabel = __('Decrease quantity', 'enterprise-booking-system'),
	incrementLabel = __('Increase quantity', 'enterprise-booking-system'),
	showLabel = false,
	size = 'medium',
}: QuantityInputProps) => {
	const generatedId = useId();
	const inputId = id ?? `qty-input-${generatedId}`;
	const errorId = `${inputId}-error`;

	const handleDecrement = () => {
		if (value > min && !disabled) {
			onChange(value - 1);
		}
	};

	const handleIncrement = () => {
		if (value < max && !disabled) {
			onChange(value + 1);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = Number.parseInt(e.target.value, 10);

		if (e.target.value === '') {
			return;
		}

		if (!Number.isNaN(newValue)) {
			const clampedValue = Math.max(min, Math.min(max, newValue));
			onChange(clampedValue);
		}
	};

	const handleInputBlur = () => {
		if (value < min) {
			onChange(min);
		}
	};

	const sizeClass = `ebs-qty-input--${size}`;
	const errorClass = error ? 'ebs-qty-input--error' : '';

	return (
		<div className={`ebs-qty-input ${sizeClass} ${errorClass} ${className}`.trim()}>
			{showLabel && label ? (
				<label htmlFor={inputId} className="ebs-qty-input__label">
					{label}
				</label>
			) : null}

			<div
				className="ebs-qty-input__controls"
				role="group"
				aria-labelledby={showLabel && label ? inputId : undefined}
			>
				<button
					type="button"
					className="ebs-qty-input__btn ebs-qty-input__btn--decrement"
					onClick={handleDecrement}
					disabled={disabled || value <= min}
					aria-label={decrementLabel}
					tabIndex={-1}
				>
					<span aria-hidden="true">−</span>
				</button>

				<input
					id={inputId}
					type="number"
					className="ebs-qty-input__input"
					value={value}
					onChange={handleInputChange}
					onBlur={handleInputBlur}
					min={min}
					max={max}
					disabled={disabled}
					aria-label={!showLabel && label ? label : undefined}
					aria-invalid={Boolean(error)}
					aria-describedby={error ? errorId : undefined}
					readOnly={disabled}
				/>

				<button
					type="button"
					className="ebs-qty-input__btn ebs-qty-input__btn--increment"
					onClick={handleIncrement}
					disabled={disabled || value >= max}
					aria-label={incrementLabel}
					tabIndex={-1}
				>
					<span aria-hidden="true">+</span>
				</button>
			</div>

			{error ? (
				<span id={errorId} className="ebs-qty-input__error" role="alert">
					{error}
				</span>
			) : null}
		</div>
	);
};
